package com.playoffpulse.frontend.routing

import com.playoffpulse.frontend.ui.StandingsObjective
import com.playoffpulse.frontend.ui.StandingsSort
import com.playoffpulse.frontend.ui.StandingsSortDirection
import com.playoffpulse.frontend.ui.StandingsSortKey
import com.playoffpulse.frontend.ui.StandingsViewMode
import com.playoffpulse.shared.logic.queryValue
import com.playoffpulse.shared.logic.blockedShotsMissesModeFromQueryValue
import com.playoffpulse.shared.model.BlockedShotsMissesMode
import com.playoffpulse.shared.model.ImportantGamesWindow

internal const val ALL_TEAMS_QUERY_VALUE = "ALL"

internal data class TeamPageUrlState(
    val gamesWindow: ImportantGamesWindow = ImportantGamesWindow.TODAY,
    val immediateOnly: Boolean = defaultImmediateOnly(ImportantGamesWindow.TODAY),
    val gamesTeam: String? = null,
)

internal data class StandingsPageUrlState(
    val objective: StandingsObjective = StandingsObjective.PLAYOFF,
    val viewMode: StandingsViewMode = StandingsViewMode.LEAGUE,
    val sort: StandingsSort = defaultStandingsSort(StandingsObjective.PLAYOFF),
    val section: String? = null,
)

internal data class ChartPageUrlState(
    val missesMode: BlockedShotsMissesMode = BlockedShotsMissesMode.INCLUDE,
)

internal data class SignalAttributionState(
    val source: String? = null,
    val signalSlug: String? = null,
    val attributedTeamId: String? = null,
)

internal data class ParsedNhlAppUrlState(
    val team: TeamPageUrlState = TeamPageUrlState(),
    val standings: StandingsPageUrlState = StandingsPageUrlState(),
    val charts: ChartPageUrlState = ChartPageUrlState(),
    val signalAttribution: SignalAttributionState = SignalAttributionState(),
)

internal object NhlAppUrlState {
    fun parse(search: String): ParsedNhlAppUrlState {
        val params = parseQuery(search)
        val teamState = parseTeamState(params)
        val standingsState = parseStandingsState(params)
        val chartState = parseChartState(params)
        val signalAttribution = parseSignalAttribution(params)
        return ParsedNhlAppUrlState(
            team = teamState,
            standings = standingsState,
            charts = chartState,
            signalAttribution = signalAttribution,
        )
    }

    fun teamQuery(state: TeamPageUrlState): String {
        val params = linkedMapOf<String, String>()
        if (state.gamesWindow != ImportantGamesWindow.TODAY) {
            params["games"] = state.gamesWindow.name.lowercase()
        }
        if (state.immediateOnly != defaultImmediateOnly(state.gamesWindow)) {
            params["immediate"] = if (state.immediateOnly) "1" else "0"
        }
        state.gamesTeam
            ?.takeIf { state.gamesWindow == ImportantGamesWindow.REMAINING }
            ?.takeIf { it != ALL_TEAMS_QUERY_VALUE }
            ?.let { params["gamesTeam"] = it.uppercase() }
        return buildQuery(params)
    }

    fun standingsQuery(state: StandingsPageUrlState): String {
        val params = linkedMapOf<String, String>()
        if (state.objective != StandingsObjective.PLAYOFF) {
            params["objective"] = state.objective.name.lowercase()
        }
        if (state.viewMode != StandingsViewMode.LEAGUE) {
            params["view"] = state.viewMode.name.lowercase()
        }
        state.section
            ?.trim()
            ?.lowercase()
            ?.takeIf { it.isNotBlank() && state.viewMode != StandingsViewMode.LEAGUE }
            ?.let { params["section"] = it }
        val defaultSort = defaultStandingsSort(state.objective)
        if (state.sort.key != defaultSort.key) {
            params["sort"] = state.sort.key.name.lowercase()
        }
        if (state.sort.direction != defaultSort.direction) {
            params["dir"] = state.sort.direction.name.lowercase()
        }
        return buildQuery(params)
    }

    fun chartsQuery(state: ChartPageUrlState): String {
        val params = linkedMapOf<String, String>()
        if (state.missesMode != BlockedShotsMissesMode.INCLUDE) {
            params["misses"] = state.missesMode.queryValue()
        }
        return buildQuery(params)
    }

    private fun parseTeamState(params: Map<String, String>): TeamPageUrlState {
        val gamesWindow = when (params["games"]?.lowercase()) {
            "week" -> ImportantGamesWindow.WEEK
            "remaining" -> ImportantGamesWindow.REMAINING
            else -> ImportantGamesWindow.TODAY
        }
        val immediateOnly = when (params["immediate"]) {
            "1", "true" -> true
            "0", "false" -> false
            else -> defaultImmediateOnly(gamesWindow)
        }
        val gamesTeam = params["gamesTeam"]
            ?.uppercase()
            ?.takeIf { gamesWindow == ImportantGamesWindow.REMAINING }
            ?.takeUnless { it == ALL_TEAMS_QUERY_VALUE }
        return TeamPageUrlState(
            gamesWindow = gamesWindow,
            immediateOnly = immediateOnly,
            gamesTeam = gamesTeam,
        )
    }

    private fun parseStandingsState(params: Map<String, String>): StandingsPageUrlState {
        val objective = when (params["objective"]?.lowercase()) {
            "draft" -> StandingsObjective.DRAFT
            else -> StandingsObjective.PLAYOFF
        }
        val viewMode = when (params["view"]?.lowercase()) {
            "conference" -> StandingsViewMode.CONFERENCE
            "division" -> StandingsViewMode.DIVISION
            else -> StandingsViewMode.LEAGUE
        }
        val section = params["section"]
            ?.trim()
            ?.lowercase()
            ?.takeIf { it.isNotBlank() && viewMode != StandingsViewMode.LEAGUE }
        val defaultSort = defaultStandingsSort(objective)
        val sortKey = when (params["sort"]?.lowercase()) {
            "team" -> StandingsSortKey.TEAM
            "max" -> StandingsSortKey.MAX
            "pts" -> StandingsSortKey.PTS
            else -> defaultSort.key
        }
        val direction = when (params["dir"]?.lowercase()) {
            "asc" -> StandingsSortDirection.ASC
            "desc" -> StandingsSortDirection.DESC
            else -> if (sortKey == defaultSort.key) defaultSort.direction else defaultStandingsSortDirection(sortKey, objective)
        }
        return StandingsPageUrlState(
            objective = objective,
            viewMode = viewMode,
            sort = StandingsSort(key = sortKey, direction = direction),
            section = section,
        )
    }

    private fun parseChartState(params: Map<String, String>): ChartPageUrlState {
        return ChartPageUrlState(
            missesMode = blockedShotsMissesModeFromQueryValue(params["misses"]) ?: BlockedShotsMissesMode.INCLUDE,
        )
    }

    private fun parseSignalAttribution(params: Map<String, String>): SignalAttributionState {
        if (!params["src"].equals("signal", ignoreCase = true)) {
            return SignalAttributionState()
        }
        return SignalAttributionState(
            source = "signal",
            signalSlug = params["signal"]?.takeIf { it.isNotBlank() },
            attributedTeamId = params["attributedTeam"]?.uppercase()?.takeIf { it.isNotBlank() },
        )
    }

    private fun buildQuery(params: Map<String, String>): String {
        if (params.isEmpty()) return ""
        return params.entries.joinToString("&", prefix = "?") { "${it.key}=${it.value}" }
    }

    private fun parseQuery(search: String): Map<String, String> {
        val trimmed = search.trim().removePrefix("?")
        if (trimmed.isBlank()) return emptyMap()
        return trimmed.split("&")
            .mapNotNull { entry ->
                if (entry.isBlank()) return@mapNotNull null
                val key = entry.substringBefore("=").trim()
                if (key.isBlank()) return@mapNotNull null
                val value = entry.substringAfter("=", "").trim()
                key to value
            }
            .toMap()
    }
}

internal fun defaultImmediateOnly(window: ImportantGamesWindow): Boolean =
    window != ImportantGamesWindow.REMAINING

private fun defaultStandingsSort(
    objective: StandingsObjective,
): StandingsSort = StandingsSort(
    key = StandingsSortKey.PTS,
    direction = defaultStandingsSortDirection(StandingsSortKey.PTS, objective),
)

private fun defaultStandingsSortDirection(
    key: StandingsSortKey,
    objective: StandingsObjective,
): StandingsSortDirection = when (objective) {
    StandingsObjective.PLAYOFF -> when (key) {
        StandingsSortKey.TEAM -> StandingsSortDirection.ASC
        StandingsSortKey.PTS, StandingsSortKey.MAX -> StandingsSortDirection.DESC
    }

    StandingsObjective.DRAFT -> when (key) {
        StandingsSortKey.TEAM -> StandingsSortDirection.ASC
        StandingsSortKey.PTS, StandingsSortKey.MAX -> StandingsSortDirection.ASC
    }
}
